import Vue from "vue";
import Vuex from "vuex";
import router from "../router";
import i18n from "../i18n";
import * as api from "./../helpers/api";
import dayjs from "dayjs";

Vue.use(Vuex);

let $store = null;

const state = {
	webinarsList: [],
	filteredWebinarsList: [],
	pastWebinarsCounter: 0,
	upcomingWebinarsCounter: 0,
	checkedFilter: [],
	activeFilters: {},
	filters: {},
	availableFilters: {},
	calendarWebinars: [],
	selectedDate: {},
	filtersInfo: {},
	query: {},
	localQueryParams: {},
	currentPage: 1,
	queryNotFound: false,
};

// actions
const actions = {
	//get main data
	async getData({ dispatch }) {
		//for access to global Store
		$store = this;
		try {
			await api.getWebinars().then((response) => {
				if (response.data) {
					dispatch("setData", response.data);
				}
			});
		} catch (error) {
			console.error(error);
		}
	},
	// Alias for getData to maintain compatibility with updated components
	fetchWebinars({ dispatch }) {
		dispatch("getData");
	},
	// Initialize filters from the store
	initializeFilters({ dispatch }) {
		// This is just an alias for now, as the filters are already initialized in setData
		// We can add specific initialization logic here if needed in the future
	},
	setData: ({ commit, dispatch }, response) => {
		const webinarsList = [...response.webinars];
		const filters = { ...response.filters };
		// set data to store
		commit("SET_DATA", { webinarsList, filters });
		//get data about filters
		dispatch("setFiltersInfo");

		// Check if there are query parameters in the URL
		if (Object.keys(router.currentRoute.query).length) {
			// Load query parameters and apply filters
			dispatch("loadQueryParams", router.currentRoute.query);
		} else {
			//set query params from URL
			dispatch("setQueryParams");
			//filter webinars
			dispatch("filterWebinars");
			// Set upcoming and past webinars
			dispatch("setUpcomingPastWebinars");
			// Set webinars to calendar
			dispatch("setWebinarsToCalendar", webinarsList);
		}
	},
	setFiltersInfo: ({ commit, state }) => {
		const filtersInfo = {};
		const availableFilters = {};

		// Collect filter values that are used by actual webinars
		state.webinarsList.forEach((webinar) => {
			if (!webinar.filters) return;

			for (let filterCategory in webinar.filters) {
				if (!availableFilters[filterCategory]) {
					availableFilters[filterCategory] = new Set();
				}

				webinar.filters[filterCategory].forEach((filterValue) => {
					const value = typeof filterValue === "object" ? filterValue.label : filterValue;
					availableFilters[filterCategory].add(value);
				});
			}
		});

		// Process each filter category
		for (let filterCategory in state.filters) {
			filtersInfo[filterCategory] = {
				id: getFilterId(filterCategory),
				items: {},
			};

			// Only include filter items that are used by at least one webinar
			const filteredItems = state.filters[filterCategory].filter((filterItem) => {
				// Check if this filter category has any webinars using it
				return availableFilters[filterCategory] && availableFilters[filterCategory].has(filterItem.label);
			});

			filteredItems.forEach((filterItem, index) => {
				filtersInfo[filterCategory].items[filterItem.label] = {
					id: index,
					count: 0,
				};
			});
		}

		commit("SET_FILTERS_INFO", filtersInfo);

		// Update filters to include only those that have webinars
		const updatedFilters = {};
		for (let filterCategory in state.filters) {
			const filteredCategoryItems = state.filters[filterCategory].filter((filterItem) => {
				return filtersInfo[filterCategory].items[filterItem.label] !== undefined;
			});

			// Only include non-empty filter categories
			if (filteredCategoryItems.length > 0) {
				updatedFilters[filterCategory] = filteredCategoryItems;
			}
		}

		commit("UPDATE_FILTERS", updatedFilters);

		function getFilterId(filterCategory) {
			return Object.keys(state.filters).indexOf(filterCategory);
		}
	},
	setQueryParams: ({ commit, state }) => {
		const query = router.currentRoute.query;
		commit("SET_QUERY", query);
		// set active filters from query
		for (let filterCategory in state.filters) {
			if (query[filterCategory]) {
				const queryFilterValues = query[filterCategory].split(",");
				queryFilterValues.forEach((queryFilterValue) => {
					const filterIndex = getFilterIndex(queryFilterValue, state.filters[filterCategory]);
					if (filterIndex !== -1) {
						commit("SET_ACTIVE_FILTER", {
							filterCategory,
							filterIndex,
						});
					}
				});
			}
		}

		function getFilterIndex(filterLabel, filterItems) {
			return filterItems.findIndex((item) => item.label === filterLabel);
		}
	},
	filterWebinars: ({ commit, state, dispatch }) => {
		let filteredWebinarsList = [...state.webinarsList];

		// If there are no active filters, return all webinars
		if (Object.keys(state.activeFilters).length === 0) {
			commit("SET_FILTERED_WEBINARS_LIST", filteredWebinarsList);
			// Update calendar events with all webinars
			dispatch("setWebinarsToCalendar", filteredWebinarsList);
			return;
		}

		// Filter webinars by active filters
		for (let filterCategory in state.activeFilters) {
			if (state.activeFilters[filterCategory].length) {
				filteredWebinarsList = filteredWebinarsList.filter((webinar) => {
					// Check if webinar has filters for this category
					if (!webinar.filters || !webinar.filters[filterCategory]) {
						return false;
					}

					// Get the webinar's filter values for this category
					const webinarFilterValues = webinar.filters[filterCategory];

					// Check if any of the webinar's filter values match any of the active filter values
					const hasMatch = webinarFilterValues.some((filterValue) => {
						// Handle both string and object filter values
						const value = typeof filterValue === "object" ? filterValue.label : filterValue;

						// Check if this value is in the active filters (case-insensitive)
						const isMatch = state.activeFilters[filterCategory].some((activeFilter) => {
							// Convert both to lowercase for case-insensitive comparison
							const activeFilterLower = activeFilter.toLowerCase();
							const valueLower = value.toLowerCase();

							// Check for exact match or if one contains the other
							return activeFilterLower === valueLower || activeFilterLower.includes(valueLower) || valueLower.includes(activeFilterLower);
						});

						return isMatch;
					});

					return hasMatch;
				});
			}
		}

		commit("SET_FILTERED_WEBINARS_LIST", filteredWebinarsList);
		// Update calendar events with filtered webinars
		dispatch("setWebinarsToCalendar", filteredWebinarsList);
	},
	setUpcomingPastWebinars: ({ commit, state }) => {
		const upcomingWebinars = state.filteredWebinarsList.filter((webinar) => webinar.status === "upcoming");
		const pastWebinars = state.filteredWebinarsList.filter((webinar) => webinar.status === "past");

		// Count upcoming and past webinars
		const upcomingWebinarsCounter = upcomingWebinars.length;
		const pastWebinarsCounter = pastWebinars.length;

		// Add featured webinar to count if it exists
		let totalUpcomingCounter = upcomingWebinarsCounter;
		if (window.webinars_data.featured_webinar && window.webinars_data.featured_webinar.title) {
			totalUpcomingCounter += 1;
		}

		commit("SET_WEBINARS_COUNTER", {
			upcomingWebinarsCounter: totalUpcomingCounter,
			pastWebinarsCounter,
		});
	},
	setActiveFilter: ({ commit, dispatch, state }, { filterCategory, filterIndex }) => {
		commit("SET_ACTIVE_FILTER", { filterCategory, filterIndex });
		dispatch("updateQueryParams");
		dispatch("filterWebinars");
		dispatch("setUpcomingPastWebinars");
	},
	removeActiveFilter: ({ commit, dispatch, state }, { filterCategory, filterIndex }) => {
		commit("REMOVE_ACTIVE_FILTER", { filterCategory, filterIndex });
		dispatch("updateQueryParams");
		dispatch("filterWebinars");
		dispatch("setUpcomingPastWebinars");
	},
	clearAllFilters: ({ commit, dispatch }) => {
		commit("CLEAR_ALL_FILTERS");
		dispatch("updateQueryParams");
		dispatch("filterWebinars");
		dispatch("setUpcomingPastWebinars");
	},
	updateQueryParams: ({ state }) => {
		const query = {};
		// Add active filters to query
		for (let filterCategory in state.activeFilters) {
			if (state.activeFilters[filterCategory].length) {
				query[filterCategory] = state.activeFilters[filterCategory].join(",");
			}
		}

		// Add date range to query if selected
		if (state.selectedDate && state.selectedDate.start && state.selectedDate.end) {
			// Format dates as YYYY-MM-DD for URL
			const formatDate = (date) => {
				const d = new Date(date);
				return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
			};

			query.date = `${formatDate(state.selectedDate.start)},${formatDate(state.selectedDate.end)}`;
		}

		router.push({ query }).catch(() => {});
	},
	setPageParams: ({ commit }, page) => {
		commit("SET_CURRENT_PAGE", page);
	},
	loadQueryParams: ({ commit, dispatch }, query) => {
		commit("SET_QUERY", query);

		// Process date parameter if present
		if (query.date) {
			const dateParts = query.date.split(",");
			if (dateParts.length === 2) {
				// Create date object with start and end dates
				const date = {
					start: dateParts[0].replace(/-/g, "/"), // Replace hyphens with slashes for cross-browser compatibility
					end: dateParts[1].replace(/-/g, "/"),
				};

				// Send the received date to the store to draw the tag
				dispatch("setDateToStore", date);
				return; // Return early as setDateToStore already handles filtering
			}
		}

		// If no date parameter or invalid format, proceed with normal filtering
		dispatch("filterWebinars");
		dispatch("setUpcomingPastWebinars");
	},
	setDateQueryParams: ({ dispatch, commit }, date) => {
		if (date) {
			// If date range is selected, send it to store
			dispatch("setDateToStore", date);
		} else {
			// If date range is cleared, remove date filter
			dispatch("removeDateFilters");
		}
		// Update pager
		commit("SET_CURRENT_PAGE", 1);
	},
	setDateToStore: ({ dispatch, commit }, date) => {
		// We get the date from query, translate it into the desired format
		let dateItem = Object.assign({}, date);
		// Get locale for dayjs formatting
		const locale = window.webinars_data.locale;

		// Create Date objects (these are interpreted in local time by default)
		let start = new Date(dateItem.start);
		let end = new Date(dateItem.end);

		// Set end time to end of day for correct range comparison
		end.setHours(23, 59, 59, 999);

		// Use Day.js for formatting to match Webinar-item.vue logic
		const dayjsFormat = "MMMM D"; // Match visibleDateText format

		// If we chose the same day in range (compare local dates)
		if (start.getDate() === end.getDate() && start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
			// Then write in short format using Day.js
			dateItem.value = dayjs(start).locale(locale).format(dayjsFormat);
		} else {
			// If the days are different - then write them through a dash using Day.js
			dateItem.value = `${dayjs(start).locale(locale).format(dayjsFormat)} - ${dayjs(end).locale(locale).format(dayjsFormat)}`;
		}

		// Save original date objects (local time) for the date picker and filtering
		dateItem.start = start;
		dateItem.end = end;

		// Write new data to store
		commit("SET_SELECTED_DATE", dateItem);

		// Filter webinars by date (now uses local time comparison)
		dispatch("filterByDate");
	},
	removeDateFilters: ({ commit, dispatch }) => {
		// Clear selected date
		commit("SET_SELECTED_DATE", {});

		// Reset current page
		commit("SET_CURRENT_PAGE", 1);

		// Filter webinars without date filter
		dispatch("filterWebinars");
		dispatch("setUpcomingPastWebinars");

		// Update URL (will remove date parameters)
		dispatch("updateQueryParams");
	},
	removeAllFilters: ({ commit, dispatch, state }) => {
		commit("CLEAR_ALL_FILTERS");
		dispatch("removeDateFilters");
		// Update calendar events with all webinars
		dispatch("setWebinarsToCalendar", state.webinarsList);
	},
	setWebinarsToCalendar: ({ commit }, webinarsList) => {
		// create current date
		const today = {
			key: "today",
			dates: new Date(),
			highlight: "blue",
		};

		// we go through all the webinars and add them to the calendar
		let calendarWebinars = webinarsList.map((webinar) => {
			// Use the date_gmt timestamp (same as used for display in Webinar-item)
			// new Date() will interpret this in the user's local timezone.
			const dateForCalendar = new Date(webinar.date_gmt * 1000);

			return {
				key: "webinars",
				dates: dateForCalendar, // Use local date based on date_gmt
				highlight: "gray",
				id: webinar.id,
				title: webinar.title,
			};
		});
		calendarWebinars.push(today);
		commit("SET_WEBINARS_TO_CALENDAR", calendarWebinars);
	},
	filterByDate: ({ commit, dispatch, state }) => {
		// Get filtered webinars list based on active filters
		let baseList = Object.keys(state.activeFilters).length > 0 ? state.filteredWebinarsList : state.webinarsList;

		let filteredWebinarsList = [...baseList]; // Work on a copy

		// Filter by date range if selected
		if (state.selectedDate && state.selectedDate.start && state.selectedDate.end) {
			// Get the selected start and end dates from the calendar (these are local Date objects)
			const rangeStartLocal = state.selectedDate.start;
			const rangeEndLocal = state.selectedDate.end;
			// Ensure end range covers the whole day
			rangeEndLocal.setHours(23, 59, 59, 999);

			filteredWebinarsList = filteredWebinarsList.filter((webinar) => {
				// Create the webinar's local date object from the date_gmt timestamp
				// mimicking the logic in Webinar-item.vue
				const webinarDateLocal = new Date(webinar.date_gmt * 1000);

				// Check if webinar's local date is within the selected local range (inclusive)
				return webinarDateLocal >= rangeStartLocal && webinarDateLocal <= rangeEndLocal;
			});
		}

		// Update filtered webinars list
		commit("SET_FILTERED_WEBINARS_LIST", filteredWebinarsList);

		// Update upcoming and past webinars counters
		dispatch("setUpcomingPastWebinars");

		// Update calendar events with filtered webinars (now using date_gmt)
		dispatch("setWebinarsToCalendar", filteredWebinarsList);
	},
};

// mutations
const mutations = {
	SET_DATA: (state, { webinarsList, filters }) => {
		state.webinarsList = webinarsList;
		state.filters = filters;
	},
	SET_FILTERS_INFO: (state, filtersInfo) => {
		state.filtersInfo = filtersInfo;
	},
	SET_QUERY: (state, query) => {
		state.query = query;
	},
	SET_ACTIVE_FILTER: (state, { filterCategory, filterIndex }) => {
		if (!state.activeFilters[filterCategory]) {
			Vue.set(state.activeFilters, filterCategory, []);
		}

		const filterLabel = state.filters[filterCategory][filterIndex].label;

		if (!state.activeFilters[filterCategory].includes(filterLabel)) {
			state.activeFilters[filterCategory].push(filterLabel);
		}
	},
	REMOVE_ACTIVE_FILTER: (state, { filterCategory, filterIndex }) => {
		const filterLabel = state.filters[filterCategory][filterIndex].label;

		const index = state.activeFilters[filterCategory].indexOf(filterLabel);

		if (index !== -1) {
			state.activeFilters[filterCategory].splice(index, 1);
		}
	},
	CLEAR_ALL_FILTERS: (state) => {
		state.activeFilters = {};
	},
	SET_FILTERED_WEBINARS_LIST: (state, filteredWebinarsList) => {
		state.filteredWebinarsList = filteredWebinarsList;
	},
	SET_WEBINARS_COUNTER: (state, { upcomingWebinarsCounter, pastWebinarsCounter }) => {
		state.upcomingWebinarsCounter = upcomingWebinarsCounter;
		state.pastWebinarsCounter = pastWebinarsCounter;
	},
	SET_CURRENT_PAGE: (state, page) => {
		state.currentPage = page;
	},
	SET_SELECTED_DATE: (state, value) => {
		state.selectedDate = value;
	},
	UPDATE_FILTERS: (state, updatedFilters) => {
		state.filters = updatedFilters;
	},
	SET_WEBINARS_TO_CALENDAR: (state, calendarWebinars) => {
		state.calendarWebinars = calendarWebinars;
	},
};

// getters
const getters = {
	getWebinarsList: (state) => state.webinarsList,
	getFilteredWebinarsList: (state) => state.filteredWebinarsList,
	getUpcomingWebinars: (state) => state.filteredWebinarsList.filter((webinar) => webinar.status === "upcoming"),
	getPastWebinars: (state) => state.filteredWebinarsList.filter((webinar) => webinar.status === "past"),
	getFilters: (state) => {
		// Transform state.filters into the format expected by the Checkbox-group component
		const result = {};
		for (let filterCategory in state.filters) {
			// Only include filter categories that have items
			if (state.filters[filterCategory] && state.filters[filterCategory].length > 0) {
				result[filterCategory] = {
					id: Object.keys(state.filters).indexOf(filterCategory),
					items: state.filters[filterCategory],
				};
			}
		}
		return result;
	},
	getActiveFilters: (state) => state.activeFilters,
	getUpcomingWebinarsCounter: (state) => state.upcomingWebinarsCounter,
	getPastWebinarsCounter: (state) => state.pastWebinarsCounter,
	currentPage: (state) => state.currentPage,
	selectedDate: (state) => state.selectedDate,
	calendarEvents: (state) => state.calendarWebinars,
	filters: (state) => {
		// Transform state.filters into the format expected by the Checkbox-group component
		const result = {};
		for (let filterCategory in state.filters) {
			// Only include filter categories that have items
			if (state.filters[filterCategory] && state.filters[filterCategory].length > 0) {
				result[filterCategory] = {
					id: Object.keys(state.filters).indexOf(filterCategory),
					items: state.filters[filterCategory],
				};
			}
		}
		return result;
	},
	activeFilters: (state) => state.activeFilters,
	checkedFilter: (state) => {
		const result = [];
		for (let filterCategory in state.activeFilters) {
			if (state.activeFilters[filterCategory].length) {
				state.activeFilters[filterCategory].forEach((filterLabel) => {
					result.push({
						category: filterCategory,
						label: filterLabel,
					});
				});
			}
		}
		return result;
	},
	webinarsList: (state) => state.webinarsList,
	upcomingWebinarsCounter: (state) => state.upcomingWebinarsCounter,
	pastWebinarsCounter: (state) => state.pastWebinarsCounter,
	calendarWebinars: (state) => state.calendarWebinars,
};

export default new Vuex.Store({
	state,
	actions,
	mutations,
	getters,
});
